import React, { useEffect, useState } from 'react';
import { Card, Row, Col, Statistic, Badge, List, Progress, Alert, Tag, Spin, Button, Modal, Form, Select, Upload, Radio, Space, message, notification } from 'antd';
import { UserOutlined, TeamOutlined, ApartmentOutlined, CarOutlined, SolutionOutlined, FileExclamationOutlined, DollarOutlined, FileProtectOutlined, UploadOutlined } from '@ant-design/icons';
import { getDashboardSummary, getEmployeeDocuments, getLicenses, getVehicles, getPettyCashSummary, getUnpaidSubcontractorInvoices, getRatesheetsV2, createExcelUploadV2, processExcelUploadV2 } from '../api';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';

const Dashboard = () => {
  const [counts, setCounts] = useState({ employees: 0, clients: 0, departments: 0, vehicles: 0, subcontractors: 0 });
  const [expiringDocs, setExpiringDocs] = useState([]);
  const [expiringVehicles, setExpiringVehicles] = useState([]);
  const [expiringCompanyLicenses, setExpiringCompanyLicenses] = useState([]);
  const [pettyCash, setPettyCash] = useState({ balance: 0, income: 0, expense: 0 });
  const [invoiceAlerts, setInvoiceAlerts] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [loadingVehicles, setLoadingVehicles] = useState(false);
  const [loadingLicenses, setLoadingLicenses] = useState(false);
  const [loadingPettyCash, setLoadingPettyCash] = useState(false);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const navigate = useNavigate();
  const now = moment();
  const [form] = Form.useForm();
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [ratesheetSource, setRatesheetSource] = useState('new');
  const [ratesheetOptions, setRatesheetOptions] = useState([]);
  const [ratesheetFile, setRatesheetFile] = useState(null);
  const [timesheetFile, setTimesheetFile] = useState(null);
  const [filters, setFilters] = useState({ month: now.month() + 1, year: now.year() });
  const [lastUploadError, setLastUploadError] = useState(null);

  useEffect(() => {
    // Fetch summary counts from new endpoint
    setLoadingSummary(true);
    getDashboardSummary().then(res => {
      if (res.success && res.data.data) setCounts(res.data.data);
      setLoadingSummary(false);
    });

    // Expiry notifications (Employee Documents)
    setLoadingDocs(true);
    getEmployeeDocuments().then(res => {
      const soon = [], expired = [];
      const today = new Date();
      const docs = Array.isArray(res.data.data) ? res.data.data : [];
      docs.forEach(doc => {
        const exp = new Date(doc.expiry_date);
        if (exp < today) expired.push(doc);
        else if ((exp - today) / (1000 * 60 * 60 * 24) < 30) soon.push(doc);
      });
      setExpiringDocs([...expired, ...soon]);
      setLoadingDocs(false);
    });

    // Vehicle expiry
    setLoadingVehicles(true);
    getVehicles().then(res => {
      const soon = [], expired = [];
      const today = new Date();
      const vehicles = Array.isArray(res.data.data) ? res.data.data : [];
      vehicles.forEach(veh => {
        const exp = new Date(veh.expiry_date);
        if (exp < today) expired.push(veh);
        else if ((exp - today) / (1000 * 60 * 60 * 24) < 30) soon.push(veh);
      });
      setExpiringVehicles([...expired, ...soon]);
      setLoadingVehicles(false);
    });

    // Company license expiry
    setLoadingLicenses(true);
    getLicenses().then(res => {
      const soon = [], expired = [];
      const today = new Date();
      const licArr = Array.isArray(res.data.data) ? res.data.data : [];
      licArr.forEach(lic => {
        const exp = new Date(lic.expiry_date);
        if (exp < today) expired.push(lic);
        else if ((exp - today) / (1000 * 60 * 60 * 24) < 30) soon.push(lic);
      });
      setExpiringCompanyLicenses([...expired, ...soon]);
      setLoadingLicenses(false);
    });

    // Petty cash summary
    setLoadingPettyCash(true);
    getPettyCashSummary && getPettyCashSummary().then(res => {
      setPettyCash(res.data.data || { balance: 0, income: 0, expense: 0 });
      setLoadingPettyCash(false);
    });

    // Subcontractor invoice notifications
    setLoadingInvoices(true);
    getUnpaidSubcontractorInvoices && getUnpaidSubcontractorInvoices().then(res => {
      setInvoiceAlerts(res.data.data || []);
      setLoadingInvoices(false);
    });
  }, []);

  const openUpload = (preset) => {
    const m = preset?.month ?? filters.month;
    const y = preset?.year ?? filters.year;
    form.setFieldsValue({ month: m, year: y, ratesheet_source: 'new', previous_ratesheet_id: null });
    setRatesheetSource('new');
    setRatesheetFile(null);
    setTimesheetFile(null);
    setUploadOpen(true);
    fetchRatesheets(m, y);
  };

  const fetchRatesheets = async (month, year) => {
    const res = await getRatesheetsV2(month, year);
    setRatesheetOptions((res.data || []).map(r => ({ label: `${r.ratesheet_original_filename || ('Ratesheet #' + r.id)} (${String(r.month).padStart(2, '0')}-${r.year})`, value: r.id })));
  };

  const onUploadSubmit = async () => {
    try {
      const vals = await form.validateFields();
      if (ratesheetSource === 'new') {
        if (!ratesheetFile || !timesheetFile) {
          message.error('Choose both files');
          return;
        }
      }
      setUploadLoading(true);
      const fd = new FormData();
      fd.append('month', vals.month);
      fd.append('year', vals.year);
      fd.append('ratesheet_source', ratesheetSource);
      if (ratesheetSource === 'previous' && vals.previous_ratesheet_id) fd.append('previous_ratesheet_id', vals.previous_ratesheet_id);
      if (ratesheetSource === 'new' && ratesheetFile) fd.append('ratesheet', ratesheetFile);
      if (timesheetFile) fd.append('timesheet', timesheetFile);
      const res = await createExcelUploadV2(fd);
      if (res.success) {
        const id = res.data?.id;
        if (id) {
          const p = await processExcelUploadV2(id);
          if (p.success) {
            message.success('Uploaded and processed');
            const m = vals.month, y = vals.year;
            navigate(`/report/processing?month=${encodeURIComponent(m)}&year=${encodeURIComponent(y)}`);
          } else {
            const desc = p.message || 'Process failed';
            setLastUploadError(desc);
            notification.error({ message: 'Upload Error', description: desc, duration: 0 });
            setUploadOpen(false);
            return;
          }
        } else {
          message.success('Uploaded');
        }
        setUploadOpen(false);
      } else {
        const msg = res.message || 'Failed';
        let desc = msg;
        const missingRates = res.data?.missing_in_ratesheet;
        if (Array.isArray(missingRates)) {
          const m1 = missingRates.length ? `Missing in ratesheet: ${missingRates.join(', ')}` : '';
          desc = [msg, m1].filter(Boolean).join(' | ');
        }
        setLastUploadError(desc);
        notification.error({ message: 'Upload Error', description: desc, duration: 0 });
        message.error(msg);
        setUploadOpen(false);
      }
    } finally {
      setUploadLoading(false);
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <Space>
          <Button icon={<UploadOutlined />} onClick={() => openUpload()}>Upload</Button>
          <Button onClick={() => navigate('/report/processing')}>Report</Button>
        </Space>
      </div>
      {lastUploadError && (
        <Alert
          type="error"
          message="Upload Error"
          description={lastUploadError}
          closable
          onClose={() => setLastUploadError(null)}
          style={{ marginBottom: 16 }}
        />
      )}
      {/* Summary Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24, minHeight: 120, alignItems: 'stretch' }}>
        {loadingSummary ? (
          <Col span={24} style={{ textAlign: 'center', padding: 40 }}>
            <Spin size="large" />
          </Col>
        ) : (
          <>
            <Col xs={12} sm={8} md={4}>
              <div
                onClick={() => navigate('/employees')}
                style={{ cursor: 'pointer', borderRadius: 14, transition: 'box-shadow 0.2s', boxShadow: '0 2px 12px rgba(24,144,255,0.10)' }}
                onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 24px rgba(24,144,255,0.18)'}
                onMouseLeave={e => e.currentTarget.style.boxShadow = '0 2px 12px rgba(24,144,255,0.10)'}
              >
                <Card bordered={false} style={{ background: 'linear-gradient(135deg, #1890ff 60%, #40a9ff 100%)', color: 'white', borderRadius: 14 }}>
                  <Statistic title={<span style={{ color: 'white' }}>Employees</span>} value={counts.employees} prefix={<UserOutlined style={{ color: 'white' }} />} valueStyle={{ color: 'white' }} />
                </Card>
              </div>
            </Col>
            <Col xs={12} sm={8} md={4}>
              <div
                onClick={() => navigate('/clients')}
                style={{ cursor: 'pointer', borderRadius: 14, transition: 'box-shadow 0.2s', boxShadow: '0 2px 12px rgba(250,173,20,0.10)' }}
                onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 24px rgba(250,173,20,0.18)'}
                onMouseLeave={e => e.currentTarget.style.boxShadow = '0 2px 12px rgba(250,173,20,0.10)'}
              >
                <Card bordered={false} style={{ background: 'linear-gradient(135deg, #faad14 60%, #ffd666 100%)', color: 'white', borderRadius: 14 }}>
                  <Statistic title={<span style={{ color: 'white' }}>Clients</span>} value={counts.clients} prefix={<TeamOutlined style={{ color: 'white' }} />} valueStyle={{ color: 'white' }} />
                </Card>
              </div>
            </Col>
            <Col xs={12} sm={8} md={4}>
              <div
                onClick={() => navigate('/departments')}
                style={{ cursor: 'pointer', borderRadius: 14, transition: 'box-shadow 0.2s', boxShadow: '0 2px 12px rgba(82,196,26,0.10)' }}
                onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 24px rgba(82,196,26,0.18)'}
                onMouseLeave={e => e.currentTarget.style.boxShadow = '0 2px 12px rgba(82,196,26,0.10)'}
              >
                <Card bordered={false} style={{ background: 'linear-gradient(135deg, #52c41a 60%, #b7eb8f 100%)', color: 'white', borderRadius: 14 }}>
                  <Statistic title={<span style={{ color: 'white' }}>Company</span>} value={counts.departments} prefix={<ApartmentOutlined style={{ color: 'white' }} />} valueStyle={{ color: 'white' }} />
                </Card>
              </div>
            </Col>
            <Col xs={12} sm={8} md={4}>
              <div
                onClick={() => navigate('/vehicles')}
                style={{ cursor: 'pointer', borderRadius: 14, transition: 'box-shadow 0.2s', boxShadow: '0 2px 12px rgba(235,47,150,0.10)' }}
                onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 24px rgba(235,47,150,0.18)'}
                onMouseLeave={e => e.currentTarget.style.boxShadow = '0 2px 12px rgba(235,47,150,0.10)'}
              >
                <Card bordered={false} style={{ background: 'linear-gradient(135deg, #eb2f96 60%, #ffadd2 100%)', color: 'white', borderRadius: 14 }}>
                  <Statistic title={<span style={{ color: 'white' }}>Vehicles</span>} value={counts.vehicles} prefix={<CarOutlined style={{ color: 'white' }} />} valueStyle={{ color: 'white' }} />
                </Card>
              </div>
            </Col>
            <Col xs={12} sm={8} md={4}>
              <div
                onClick={() => navigate('/subcontractors')}
                style={{ cursor: 'pointer', borderRadius: 14, transition: 'box-shadow 0.2s', boxShadow: '0 2px 12px rgba(114,46,209,0.10)' }}
                onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 24px rgba(114,46,209,0.18)'}
                onMouseLeave={e => e.currentTarget.style.boxShadow = '0 2px 12px rgba(114,46,209,0.10)'}
              >
                <Card bordered={false} style={{ background: 'linear-gradient(135deg, #722ed1 60%, #b37feb 100%)', color: 'white', borderRadius: 14 }}>
                  <Statistic title={<span style={{ color: 'white' }}>Subcontractors</span>} value={counts.subcontractors} prefix={<SolutionOutlined style={{ color: 'white' }} />} valueStyle={{ color: 'white' }} />
                </Card>
              </div>
            </Col>
          </>
        )}
      </Row>

      {/* Expiry Notifications */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} md={8}>
          <Card title={<span><FileExclamationOutlined /> Employee Document Expiry</span>} style={{ minHeight: 340, maxHeight: 340, height: 340, display: 'flex', flexDirection: 'column', border: '1.5px solid #e0e0e0' }}>
            {loadingDocs ? <Spin style={{ display: 'block', margin: '40px auto' }} /> : (
            <List
              dataSource={expiringDocs}
              style={{ maxHeight: 260, overflowY: 'auto' }}
              renderItem={item => {
                const exp = new Date(item.expiry_date);
                const today = new Date();
                let color = 'green', text = 'Valid';
                if (exp < today) { color = 'red'; text = 'Expired'; }
                else if ((exp - today) / (1000 * 60 * 60 * 24) < 30) { color = 'orange'; text = 'Expiring Soon'; }
                return (
                  <List.Item
                    style={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/employees/${item.employee_id}/edit`)}
                  >
                    <Badge color={color} text={<span>{item.document_type} - {item.employee_name || ''} <Tag color={color}>{text}</Tag> <span style={{ fontSize: 12, color: '#888' }}>({item.expiry_date})</span></span>} />
                  </List.Item>
                );
              }}
              locale={{ emptyText: 'No expiring documents' }}
            />
            )}
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card title={<span><FileProtectOutlined /> Vehicle Expiry</span>} style={{ minHeight: 340, maxHeight: 340, height: 340, display: 'flex', flexDirection: 'column', border: '1.5px solid #e0e0e0' }}>
            {loadingVehicles ? <Spin style={{ display: 'block', margin: '40px auto' }} /> : (
            <List
              dataSource={expiringVehicles}
              style={{ maxHeight: 260, overflowY: 'auto' }}
              renderItem={item => {
                const exp = new Date(item.expiry_date);
                const today = new Date();
                let color = 'green', text = 'Valid';
                if (exp < today) { color = 'red'; text = 'Expired'; }
                else if ((exp - today) / (1000 * 60 * 60 * 24) < 30) { color = 'orange'; text = 'Expiring Soon'; }
                return (
                  <List.Item
                    style={{ cursor: 'pointer' }}
                    onClick={() => navigate('/vehicles', { state: { editVehicleId: item.id } })}
                  >
                    <Badge color={color} text={<span>{item.reg_no || item.vehicle || ''} <Tag color={color}>{text}</Tag> <span style={{ fontSize: 12, color: '#888' }}>({item.expiry_date})</span></span>} />
                  </List.Item>
                );
              }}
              locale={{ emptyText: 'No expiring vehicles' }}
            />
            )}
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card title={<span><FileProtectOutlined /> Company License Expiry</span>} style={{ minHeight: 340, maxHeight: 340, height: 340, display: 'flex', flexDirection: 'column', border: '1.5px solid #e0e0e0' }}>
            {loadingLicenses ? <Spin style={{ display: 'block', margin: '40px auto' }} /> : (
            <List
              dataSource={expiringCompanyLicenses}
              style={{ maxHeight: 260, overflowY: 'auto' }}
              renderItem={item => {
                const exp = new Date(item.expiry_date);
                const today = new Date();
                let color = 'green', text = 'Valid';
                if (exp < today) { color = 'red'; text = 'Expired'; }
                else if ((exp - today) / (1000 * 60 * 60 * 24) < 30) { color = 'orange'; text = 'Expiring Soon'; }
                return (
                  <List.Item
                    style={{ cursor: 'pointer' }}
                    onClick={() => navigate('/licenses', { state: { editLicenseId: item.id } })}
                  >
                    <Badge color={color} text={<span>{item.licence_no || item.company || ''} <Tag color={color}>{text}</Tag> <span style={{ fontSize: 12, color: '#888' }}>({item.expiry_date})</span></span>} />
                  </List.Item>
                );
              }}
              locale={{ emptyText: 'No expiring company licenses' }}
            />
            )}
          </Card>
        </Col>
      </Row>

      {/* Petty Cash Summary & Subcontractor Invoice Notification */}
      <Row gutter={[16, 16]}>
        <Col xs={24} md={12}>
          <div
            onClick={() => navigate('/petty-cash')}
            style={{ cursor: 'pointer', borderRadius: 14 }}
          >
            <Card title={<span><DollarOutlined /> Petty Cash Summary</span>} style={{ minHeight: 340, border: '1.5px solid #e0e0e0' }}>
              {loadingPettyCash ? <Spin style={{ display: 'block', margin: '40px auto' }} /> : (
              <>
              <Statistic title="Current Balance" value={pettyCash.balance} precision={2} prefix="AED" valueStyle={{ color: '#52c41a' }} />
              <div style={{ marginTop: 12 }}>
                <Progress percent={pettyCash.income ? (pettyCash.expense / pettyCash.income) * 100 : 0} status="active" showInfo={false} />
                <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
                  Income: <b>AED {pettyCash.income}</b> &nbsp; | &nbsp; Expense: <b>AED {pettyCash.expense}</b>
                </div>
              </div>
              </>
              )}
            </Card>
          </div>
        </Col>
        <Col xs={24} md={12}>
          <div
            onClick={() => navigate('/subcontractors')}
            style={{ cursor: 'pointer', borderRadius: 14 }}
          >
            <Card title={<span><FileExclamationOutlined /> Subcontractor Invoice Alerts</span>} style={{ minHeight: 340, border: '1.5px solid #e0e0e0' }}>
              {loadingInvoices ? <Spin style={{ display: 'block', margin: '40px auto' }} /> : (
              <List
                dataSource={invoiceAlerts}
                renderItem={item => (
                  <List.Item>
                    <Badge color={item.status === 'overdue' ? 'red' : 'orange'} text={<span>{item.subcontractor?.name || ''} <Tag color={item.status === 'overdue' ? 'red' : 'orange'}>{item.status === 'overdue' ? 'Overdue' : 'Unpaid'}</Tag> <span style={{ fontSize: 12, color: '#888' }}>({item.invoice_file})</span></span>} />
                  </List.Item>
                )}
                locale={{ emptyText: 'No invoice alerts' }}
              />
              )}
            </Card>
          </div>
        </Col>
      </Row>
      <Modal open={uploadOpen} title="Upload Excel" onCancel={() => setUploadOpen(false)} onOk={onUploadSubmit} confirmLoading={uploadLoading}>
        <Form form={form} layout="vertical" initialValues={{ month: filters.month, year: filters.year, ratesheet_source: 'new' }}>
          <Form.Item name="month" label="Month" rules={[{ required: true }]}>
            <Select options={Array.from({ length: 12 }, (_, i) => ({ label: moment().month(i).format('MMMM'), value: i + 1 }))} />
          </Form.Item>
          <Form.Item name="year" label="Year" rules={[{ required: true }]}>
            <Select options={Array.from({ length: 8 }, (_, i) => ({ label: String(moment().year() - 3 + i), value: moment().year() - 3 + i }))} />
          </Form.Item>
          {/* <Form.Item label="Ratesheet Source">
            <Radio.Group value={ratesheetSource} onChange={(e) => { setRatesheetSource(e.target.value); const v = form.getFieldsValue(); fetchRatesheets(v.month, v.year); }}>
              <Radio value="new">Upload New</Radio>
              <Radio value="previous">Use Previous</Radio>
            </Radio.Group>
          </Form.Item> */}
          {ratesheetSource === 'previous' ? (
            <Form.Item name="previous_ratesheet_id" label="Previous Ratesheet" rules={[{ required: true }]}>
              <Select options={ratesheetOptions} showSearch optionFilterProp="label" />
            </Form.Item>
          ) : (
            <Form.Item label="Ratesheet">
              <Upload beforeUpload={file => { setRatesheetFile(file); return false; }} showUploadList={ratesheetFile ? [{ name: ratesheetFile.name }] : false} maxCount={1} accept=".xlsx,.xls,.csv">
                <Button icon={<UploadOutlined />}>Choose File</Button>
              </Upload>
            </Form.Item>
          )}
          <Form.Item label="Timesheet">
            <Upload beforeUpload={file => { setTimesheetFile(file); return false; }} showUploadList={timesheetFile ? [{ name: timesheetFile.name }] : false} maxCount={1} accept=".xlsx,.xls,.csv">
              <Button icon={<UploadOutlined />}>Choose File</Button>
            </Upload>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Dashboard; 
